import { SERVER_URL } from "../../../constants";
import { fetchJson, fetchJsonAuth } from "../utils/http";

export const getGroupStatuses = (token) =>
  fetchJson(`${SERVER_URL}api/group-statuses?type=BOOKING`, token);

export const updateCampGroup = (groupId, body, token) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}`, token, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const upsertBookingMeta = (groupId, meta, token, exists) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/booking-meta`, token, {
    method: exists ? "PUT" : "POST",
    body: JSON.stringify(meta),
  });

// One group as the list endpoint shapes it — { id, groupName, campus, studentIds,
// adultIds, … }. For re-reading membership after a server-side change (CSV
// upload can add members and swap a placeholder for an existing student).
export const getCampGroup = (groupId, token) =>
  fetchJsonAuth(`${SERVER_URL}api/campgroups/${groupId}`, token);

export const getBookingMeta = (groupId, token) =>
  fetchJsonAuth(`${SERVER_URL}api/campgroup/${groupId}/booking-meta`, token);

// Membership window the backend expects: { start_date, end_date } as ISO dates.
// Sent only when both ends are known — with no body the backend stamps the
// membership start = end = today, which never covers the stay (the V145 bug).
const membershipWindow = (window) => {
  const start = window?.startDate;
  const end = window?.endDate;
  return start && end ? { body: JSON.stringify({ start_date: start, end_date: end }) } : {};
};

export const addStudentToGroup = (groupId, studentId, token, window) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/student/${studentId}`, token, {
    method: "PUT",
    ...membershipWindow(window),
  });

export const removeStudentFromGroup = (groupId, studentId, token) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/student/${studentId}`, token, { method: "DELETE" });

export const addAdultToGroup = (groupId, adultId, token, window) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/adult/${adultId}`, token, {
    method: "PUT",
    ...membershipWindow(window),
  });

export const removeAdultFromGroup = (groupId, adultId, token) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/adult/${adultId}`, token, { method: "DELETE" });

// Break out of group billing — flips meta.groupBilled to false so members are billed
// individually. The booking meta (default rate, dates, flight package, status) is preserved
// and can be flipped back with enableGroupBilling. Backend returns 204 on success, 409 if
// any group payment exists. Idempotent.
export const breakOutToIndividual = async (groupId, token) => {
  const res = await fetch(`${SERVER_URL}api/campgroup/${groupId}/break-out-to-individual`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (res.status === 204) return;
  const text = await res.text().catch(() => "");
  let msg = `Break out failed (${res.status})`;
  if (text) {
    try {
      const body = JSON.parse(text);
      msg = body?.message || body?.error || text;
    } catch {
      msg = text;
    }
  }
  const err = new Error(msg);
  err.status = res.status;
  throw err;
};

// Re-enable group billing on a previously broken-out group. Idempotent, no preconditions.
// Backend returns 204 on success.
export const enableGroupBilling = async (groupId, token) => {
  const res = await fetch(`${SERVER_URL}api/campgroup/${groupId}/enable-group-billing`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (res.status === 204) return;
  const text = await res.text().catch(() => "");
  let msg = `Enable group billing failed (${res.status})`;
  if (text) {
    try {
      const body = JSON.parse(text);
      msg = body?.message || body?.error || text;
    } catch {
      msg = text;
    }
  }
  const err = new Error(msg);
  err.status = res.status;
  throw err;
};
